import request from 'app/api/request';
import ServerErrorHandler from 'app/ErrorHandler';
import { useQueryClient, useMutation, useQuery, UseQueryResult, UseMutationResult } from 'react-query';
import { UUID, CalculationLinkrole, CalculationRuleSummandInput, CalculationRuleSummary } from './types';
import { taxonomyQueryKeys } from './taxonomy';
import { dispatchMessage, EventType } from 'app/useToasts';
import { i18n } from 'app/utils/i18n';

const CALC_RULE_ERROR_LABELS: Record<string, string> = {
  CALC_RULE_BALANCE_WEIGHT: 'tag.calculations.error.balance-weight',
  CALC_RULE_CYCLE: 'tag.calculations.error.cycle',
  CALC_RULE_DUPLICATE: 'tag.calculations.error.duplicate',
  CALC_RULE_SELF_REFERENCE: 'tag.calculations.error.self-reference',
};

function calculationRuleErrorHandler(error: any) {
  let message: string | null = null;
  try {
    const body = JSON.parse(error.request?.responseText);
    if (body?.code && CALC_RULE_ERROR_LABELS[body.code]) {
      message = i18n(CALC_RULE_ERROR_LABELS[body.code]);
    } else if (body?.error) {
      message = body.error;
    }
  } catch {}
  if (!message) message = i18n('tag.calculations.error.generic');
  dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: message, removeTime: 5000 });
}

export const calculationRuleQueryKeys = {
  list: 'CALCULATION_RULES',
  linkroles: 'CALCULATION_LINKROLES',
} as const;

function invalidateCalculations(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries(calculationRuleQueryKeys.list);
  queryClient.invalidateQueries(taxonomyQueryKeys.calculations);
}

export function useCalculationLinkroles(documentSuperId: number | undefined): UseQueryResult<CalculationLinkrole[]> {
  return useQuery(
    [calculationRuleQueryKeys.linkroles, documentSuperId],
    async () => {
      const res = await request.get(`/gaby/taxonomy/calculation-linkroles?documentSuperId=${documentSuperId}`);
      return (res?.data as CalculationLinkrole[]) || [];
    },
    {
      enabled: !!documentSuperId,
      staleTime: 5 * 60 * 1000,
      onError: ServerErrorHandler,
    }
  );
}

export function useCalculationRules(documentSuperId: number | undefined, taxonomyId?: string): UseQueryResult<CalculationRuleSummary[]> {
  return useQuery(
    [calculationRuleQueryKeys.list, documentSuperId, taxonomyId],
    async () => {
      const taxonomyParam = taxonomyId ? `&taxonomyId=${taxonomyId}` : '';
      const res = await request.get(`/gaby/concepts/calculation-rules?documentSuperId=${documentSuperId}${taxonomyParam}`);
      return (res?.data as CalculationRuleSummary[]) || [];
    },
    {
      enabled: !!documentSuperId,
      onError: ServerErrorHandler,
    }
  );
}

export interface CreateCalculationRuleRequest {
  documentSuperId: number;
  parentConceptId: UUID;
  linkroleUri: string | null;
  summands: CalculationRuleSummandInput[];
}

export function useCreateCalculationRule(): UseMutationResult<CalculationRuleSummary, unknown, CreateCalculationRuleRequest> {
  const queryClient = useQueryClient();
  return useMutation(
    async (body: CreateCalculationRuleRequest) => {
      const res = await request.post('/gaby/concepts/calculation-rules', body);
      return res?.data as CalculationRuleSummary;
    },
    {
      onSuccess: () => invalidateCalculations(queryClient),
      onError: calculationRuleErrorHandler,
    }
  );
}

export interface SeedCalculationRuleRequest {
  documentSuperId: number;
  parentConceptId: UUID;
  linkroleUri: string;
}

export function useSeedCalculationRule(): UseMutationResult<CalculationRuleSummary, unknown, SeedCalculationRuleRequest> {
  const queryClient = useQueryClient();
  return useMutation(
    async (body: SeedCalculationRuleRequest) => {
      const res = await request.post('/gaby/concepts/calculation-rules/seed', body);
      return res?.data as CalculationRuleSummary;
    },
    {
      onSuccess: () => invalidateCalculations(queryClient),
      onError: calculationRuleErrorHandler,
    }
  );
}

export interface UpdateCalculationRuleRequest {
  ruleId: UUID;
  documentSuperId: number;
  summands: CalculationRuleSummandInput[];
}

export function useUpdateCalculationRule(): UseMutationResult<CalculationRuleSummary, unknown, UpdateCalculationRuleRequest> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ ruleId, ...body }: UpdateCalculationRuleRequest) => {
      const res = await request.put(`/gaby/concepts/calculation-rules/${ruleId}`, body);
      return res?.data as CalculationRuleSummary;
    },
    {
      onSuccess: () => invalidateCalculations(queryClient),
      onError: calculationRuleErrorHandler,
    }
  );
}

export interface DeleteCalculationRuleRequest {
  ruleId: UUID;
  documentSuperId: number;
}

export function useDeleteCalculationRule(): UseMutationResult<unknown, unknown, DeleteCalculationRuleRequest> {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ ruleId, documentSuperId }: DeleteCalculationRuleRequest) => {
      await request.del(`/gaby/concepts/calculation-rules/${ruleId}?documentSuperId=${documentSuperId}`);
    },
    {
      onSuccess: () => invalidateCalculations(queryClient),
      onError: calculationRuleErrorHandler,
    }
  );
}
